import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { CalendarClock, Check, Clock, LogOut, Moon, Pause, ShoppingBag, Sun, SunMoon, Undo2, UserPlus, Wallet } from 'lucide-react';
import { cn } from '../utils/cn';
import { getApiErrorMessage } from '../services/api';
import { telHref, useSite } from '../utils/site';
import { customerApi, useCustomerToken, type Account, type DayChangeRequest, type DayPart, type Profile, type ProfileForm, type Shift } from './customerApi';
import { AddressFields, Field, MilkPlanPicker, addressErrors, milkReady, toChoice, type MilkDraft } from './CustomerForms';
import { rupees } from './shopApi';
import { UpiQrPay } from './UpiQrPay';

// The customer app's own screen, on the website (added on request - the
// app's customer login is to be retired): today's delivery code for the
// agent, the subscription and its day changes, the wallet / bill with
// Pay now by UPI, and both sides of the ledger. Same mobile + PIN as the app.

const inputCls = 'w-full rounded-lg border border-ink-300/50 bg-white px-3 py-2.5 text-base focus:border-brand-green-600 focus:outline-none focus:ring-2 focus:ring-brand-green-100';
const day = (iso: string, o: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' }) => new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', o);
const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const METHOD: Record<string, string> = { Cash: 'Cash', OfficeQR: 'Office QR (UPI)', WhatsAppQR: 'WhatsApp QR (UPI)', WebsiteUPI: 'Paid on the website (UPI)' };

function notice(a: Account): string | null {
  switch (a.status) {
    case 'WalletEmpty': return 'Your wallet is empty — please recharge to keep deliveries going.';
    case 'LowBalance': return `Wallet running low${a.days_remaining != null ? ` — about ${a.days_remaining} day${a.days_remaining === 1 ? '' : 's'} left` : ''}. Please recharge soon.`;
    case 'Overdue': return `${rupees(a.overdue_amount ?? 0)} is overdue${a.days_overdue ? ` by ${a.days_overdue} day${a.days_overdue === 1 ? '' : 's'}` : ''}. Please pay now.`;
    case 'Due': return `Bill of ${rupees(a.billed_outstanding ?? 0)} is due${a.next_due_date ? ` by ${day(a.next_due_date)}` : ''}.`;
    default: return null;
  }
}

function Login() {
  const site = useSite();
  const [mobile, setMobile] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await customerApi.login(mobile.replace(/\D/g, '').slice(-10), pin);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not sign in.'));
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-4">
      <form onSubmit={submit} className="flex flex-col gap-3 rounded-xl bg-white p-5">
        <h1 className="text-xl font-bold text-brand-green-900">My milk subscription</h1>
        <p className="text-sm text-ink-500">Sign in with your mobile number and PIN — the same ones as the Sri KalpaGavya app.</p>
        <label className="flex flex-col gap-1 text-sm font-medium">Mobile number
          <input className={inputCls} inputMode="numeric" autoComplete="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="98XXXXXXXX" />
        </label>
        <label className="flex flex-col gap-1 text-sm font-medium">PIN
          <input className={cn(inputCls, 'tracking-[0.4em]')} type="password" inputMode="numeric" autoComplete="current-password" maxLength={12} value={pin} onChange={(e) => setPin(e.target.value)} />
        </label>
        {error && <p className="text-sm font-medium text-critical-600" role="alert">{error}</p>}
        <button type="submit" disabled={busy || mobile.replace(/\D/g, '').length < 10 || pin.length < 4} className="rounded-xl bg-brand-green-700 py-3 font-bold text-white disabled:bg-ink-300">
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        <p className="text-xs text-ink-500">
          Forgot your PIN? {site.phone ? <>Call us on <a className="font-semibold text-brand-green-700" href={telHref(site.phone)}>{site.phone}</a></> : 'Call the farm'} and we'll send you a new one.
        </p>
      </form>
      <div className="flex flex-col gap-2 rounded-xl bg-white p-5">
        <p className="font-semibold text-brand-green-900">New customer?</p>
        <p className="text-sm text-ink-500">Create your account in 2 minutes — your address, daily milk plan and your own PIN. No need to call us.</p>
        <Link to="/shop/account/new" className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-brand-green-700 py-2.5 font-bold text-brand-green-800"><UserPlus className="h-5 w-5" /> Create account</Link>
      </div>
      <p className="text-center text-sm text-ink-500">Just want to buy once? <Link to="/shop" className="font-semibold text-brand-green-700">Order milk</Link> without an account.</p>
    </div>
  );
}

function ChangeDelivery({ plan, onDone }: { plan: DayPart[]; onDone: () => void }) {
  const qc = useQueryClient();
  const days = Array.from({ length: 10 }, (_, i) => { const d = new Date(); d.setDate(d.getDate() + i); return isoDay(d); });
  const [date, setDate] = useState(days[1]);
  const [kind, setKind] = useState<DayChangeRequest['kind']>('pause');
  const [pauseDays, setPauseDays] = useState(1);
  const am0 = Number(plan.find((p) => p.shift === 'Morning')?.litres ?? 0);
  const pm0 = Number(plan.find((p) => p.shift === 'Evening')?.litres ?? 0);
  const [am, setAm] = useState(am0);
  const [pm, setPm] = useState(pm0);
  const shifts = plan.map((p) => p.shift);
  const moveTo: Shift | null = shifts.length === 1 ? (shifts[0] === 'Morning' ? 'Evening' : 'Morning') : null;
  const save = useMutation({
    mutationFn: () => {
      const end = new Date(`${date}T00:00:00`);
      end.setDate(end.getDate() + pauseDays - 1);
      const req: DayChangeRequest = kind === 'pause' ? { kind, start_date: date, end_date: isoDay(end) }
        : kind === 'move' ? { kind, start_date: date, to_shift: moveTo! }
          : { kind, start_date: date, morning_litres: am, evening_litres: pm };
      return customerApi.makeChange(req);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cust'] }); onDone(); },
  });
  const opt = (k: DayChangeRequest['kind'], icon: React.ReactNode, label: string) => (
    <button type="button" onClick={() => setKind(k)} className={cn('flex flex-1 flex-col items-center gap-1 rounded-lg border p-2 text-xs font-semibold', kind === k ? 'border-brand-green-700 bg-brand-green-50 text-brand-green-800' : 'border-ink-300/40 text-ink-600')}>
      {icon}{label}
    </button>
  );
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-brand-green-200 bg-brand-green-50/40 p-3">
      <div className="no-scrollbar -mx-1 flex gap-1.5 overflow-x-auto px-1">
        {days.map((d, i) => (
          <button key={d} type="button" onClick={() => setDate(d)} className={cn('shrink-0 rounded-lg border px-2.5 py-1.5 text-xs', date === d ? 'border-brand-green-700 bg-white font-bold text-brand-green-800' : 'border-ink-300/40 bg-white')}>
            {i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : day(d)}
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        {opt('pause', <Pause className="h-4 w-4" />, 'Not at home')}
        {moveTo && opt('move', moveTo === 'Morning' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />, `Move to ${moveTo === 'Morning' ? 'AM' : 'PM'}`)}
        {opt('quantity', <CalendarClock className="h-4 w-4" />, 'More / less milk')}
      </div>
      {kind === 'pause' && (
        <label className="flex items-center justify-between text-sm">No milk for
          <select className="rounded-lg border border-ink-300/50 px-2 py-1.5" value={pauseDays} onChange={(e) => setPauseDays(Number(e.target.value))}>
            {[1, 2, 3, 4, 5, 6, 7, 10, 14].map((n) => <option key={n} value={n}>{n} day{n > 1 ? 's' : ''}</option>)}
          </select>
        </label>
      )}
      {kind === 'quantity' && (
        <div className="grid grid-cols-2 gap-2 text-sm">
          <label className="flex flex-col gap-1">Morning (L)<input className={inputCls} type="number" min={0} step={0.5} value={am} onChange={(e) => setAm(Number(e.target.value))} /></label>
          <label className="flex flex-col gap-1">Evening (L)<input className={inputCls} type="number" min={0} step={0.5} value={pm} onChange={(e) => setPm(Number(e.target.value))} /></label>
        </div>
      )}
      {save.isError && <p className="text-xs font-medium text-critical-600">{getApiErrorMessage(save.error)}</p>}
      <div className="flex gap-2">
        <button type="button" disabled={save.isPending} onClick={() => save.mutate()} className="flex-1 rounded-lg bg-brand-green-700 py-2.5 text-sm font-bold text-white">{save.isPending ? 'Saving…' : 'Save change'}</button>
        <button type="button" onClick={onDone} className="rounded-lg px-4 text-sm text-ink-500">Cancel</button>
      </div>
    </div>
  );
}

function PayNow({ account }: { account: Account }) {
  const qc = useQueryClient();
  const prepaid = account.billing_mode === 'Prepaid';
  const due = Math.max(0, Number(account.balance_due));
  const suggest = prepaid ? Math.max(100, Math.ceil((Number(account.daily_value ?? 0) * 30) / 50) * 50) : Math.ceil(due);
  const [amount, setAmount] = useState(suggest || 100);
  const pays = useQuery({ queryKey: ['cust', 'upi'], queryFn: customerApi.upiPayments, refetchInterval: 30_000 });
  const start = useMutation({ mutationFn: () => customerApi.startUpi(amount), onSuccess: () => qc.invalidateQueries({ queryKey: ['cust', 'upi'] }) });
  const open = pays.data?.find((p) => p.upi_link);
  const checking = pays.data?.filter((p) => p.status === 'Paid - to verify') ?? [];

  return (
    <div className="flex flex-col gap-3">
      {checking.map((p) => (
        <div key={p.id} className="flex gap-2 rounded-xl bg-warning-50 p-3 text-sm text-warning-700"><Clock className="h-5 w-5 shrink-0" />
          <span><b>{rupees(p.amount)} sent — we're checking it.</b> UPI reference {p.utr}. It shows in Payments once confirmed.</span></div>
      ))}
      {open ? (
        <>
          <UpiQrPay link={open.upi_link!} amount={open.amount} payee={open.payee_name} upiId={open.upi_id} reference={`Payment ${open.ref}`}
            notReceived={open.status === 'Not received'}
            onPaid={async (utr) => { await customerApi.claimUpi(open.id, utr); qc.invalidateQueries({ queryKey: ['cust'] }); }} />
          <button type="button" className="text-sm text-ink-500 underline" onClick={() => start.mutate()}>Pay a different amount instead</button>
        </>
      ) : (
        <div className="flex flex-col gap-2 rounded-xl bg-white p-4">
          <p className="text-sm font-semibold">{prepaid ? 'Recharge your wallet' : 'Pay your bill'} by UPI</p>
          <div className="flex gap-2">
            <span className="flex items-center rounded-lg border border-ink-300/50 bg-surface-subtle px-3 text-ink-500">₹</span>
            <input className={inputCls} type="number" min={1} inputMode="numeric" value={amount} onChange={(e) => setAmount(Number(e.target.value))} />
            <button type="button" disabled={!(amount >= 1) || start.isPending} onClick={() => start.mutate()} className="shrink-0 rounded-lg bg-brand-green-700 px-4 font-bold text-white disabled:bg-ink-300">Pay</button>
          </div>
          {start.isError && <p className="text-xs font-medium text-critical-600">{getApiErrorMessage(start.error)}</p>}
        </div>
      )}
    </div>
  );
}

// A customer without daily milk (signed up for shop orders only, or stopped) starts it here.
function StartMilk() {
  const qc = useQueryClient();
  const info = useQuery({ queryKey: ['signup-info'], queryFn: customerApi.signupInfo });
  const [open, setOpen] = useState(false);
  const [milk, setMilk] = useState<MilkDraft | null>(null);
  const start = useMutation({
    mutationFn: () => customerApi.startSubscription(toChoice(milk!), milk!.billing || undefined),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cust'] }),
  });
  const i = info.data;
  if (!i || !i.open || !i.plans.length) {
    return (
      <section className="flex flex-col gap-2 rounded-xl bg-white p-4">
        <p className="font-semibold text-brand-green-900">No daily milk yet</p>
        <p className="text-sm text-ink-500">Order from the shop any time.</p>
        <Link to="/shop" className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-green-700 py-2.5 text-sm font-bold text-white"><ShoppingBag className="h-4 w-4" /> Go to the shop</Link>
      </section>
    );
  }
  return (
    <section className="flex flex-col gap-3 rounded-xl bg-white p-4">
      <p className="flex items-center gap-2 font-semibold text-brand-green-900"><SunMoon className="h-5 w-5" /> Get fresh milk at your door every day</p>
      {!open ? (
        <>
          <p className="text-sm text-ink-500">Pick a plan, morning or evening, and a start date. You can pause or change any day.</p>
          <button type="button" onClick={() => setOpen(true)} className="rounded-lg bg-brand-green-700 py-2.5 text-sm font-bold text-white">Start daily milk</button>
          <Link to="/shop" className="text-center text-sm font-semibold text-brand-green-700">Or shop for milk & more ›</Link>
        </>
      ) : (
        <>
          <MilkPlanPicker info={i} value={milk} onChange={setMilk} />
          {start.isError && <p className="text-sm font-medium text-critical-600">{getApiErrorMessage(start.error)}</p>}
          <div className="flex gap-2">
            <button type="button" disabled={!milkReady(milk, i) || start.isPending} onClick={() => start.mutate()} className="flex-1 rounded-lg bg-brand-green-700 py-2.5 text-sm font-bold text-white disabled:bg-ink-300">
              {start.isPending ? 'Starting…' : 'Start daily milk'}
            </button>
            <button type="button" onClick={() => setOpen(false)} className="rounded-lg px-4 text-sm text-ink-500">Cancel</button>
          </div>
        </>
      )}
    </section>
  );
}

const toForm = (p: Profile): ProfileForm => ({
  name: p.name, email: p.email ?? '', apartment_name: p.apartment_name ?? '', house_number: p.house_number ?? '', address: p.address ?? '',
  city: p.city ?? '', state: p.state ?? '', pincode: p.pincode ?? '',
});

function ProfileTab() {
  const qc = useQueryClient();
  const profile = useQuery({ queryKey: ['cust', 'profile'], queryFn: customerApi.profile });
  const info = useQuery({ queryKey: ['signup-info'], queryFn: customerApi.signupInfo });
  const [form, setForm] = useState<ProfileForm | null>(null);
  const [tried, setTried] = useState(false);
  const [pins, setPins] = useState({ current: '', next: '', again: '' });
  useEffect(() => { if (profile.data && !form) setForm(toForm(profile.data)); }, [profile.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = useMutation({
    mutationFn: () => customerApi.saveProfile(form!),
    onSuccess: (p) => { qc.setQueryData(['cust', 'profile'], p); qc.invalidateQueries({ queryKey: ['cust', 'me'] }); },
  });
  const pin = useMutation({
    mutationFn: () => customerApi.changePin(pins.current, pins.next),
    onSuccess: () => setPins({ current: '', next: '', again: '' }),
  });
  if (!form || !profile.data) return <div className="h-64 animate-pulse rounded-xl bg-white" />;
  const errs = addressErrors(form, form.pincode === profile.data.pincode ? [] : info.data?.pincodes ?? []);
  const pinErr = !/^\d{4,6}$/.test(pins.next) ? 'New PIN must be 4 to 6 digits' : pins.next !== pins.again ? "The new PINs don't match" : null;
  const pinCls = 'w-full rounded-lg border border-ink-300/50 bg-white px-3 py-2.5 text-base tracking-[0.3em] focus:border-brand-green-600 focus:outline-none';

  return (
    <div className="flex flex-col gap-3">
      <form className="flex flex-col gap-3 rounded-xl bg-white p-4" onSubmit={(e) => { e.preventDefault(); setTried(true); if (form.name.trim().length >= 2 && !Object.keys(errs).length) save.mutate(); }}>
        <p className="font-semibold">My details</p>
        <Field label="Name"><input className={inputCls} value={form.name} onChange={(e) => { save.reset(); setForm({ ...form, name: e.target.value }); }} /></Field>
        <Field label="Mobile (login)"><input className={cn(inputCls, 'bg-surface-subtle text-ink-500')} value={profile.data.mobile} readOnly /></Field>
        <Field label="Email ID" hint="(optional)"><input className={inputCls} type="email" value={form.email} onChange={(e) => { save.reset(); setForm({ ...form, email: e.target.value }); }} /></Field>
        <AddressFields value={form} onChange={(v) => { save.reset(); setForm(v); }} errors={tried ? errs : {}} hasLocation={profile.data.has_location} />
        {save.isError && <p className="text-sm font-medium text-critical-600">{getApiErrorMessage(save.error)}</p>}
        <button type="submit" disabled={save.isPending} className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-green-700 py-2.5 text-sm font-bold text-white">
          {save.isSuccess ? <><Check className="h-4 w-4" /> Saved</> : save.isPending ? 'Saving…' : 'Save my details'}
        </button>
        <p className="text-xs text-ink-500">To change your mobile number or your milk plan, please call us. Day-to-day changes are on Home → Change delivery.</p>
      </form>

      <form className="flex flex-col gap-3 rounded-xl bg-white p-4" onSubmit={(e) => { e.preventDefault(); if (!pinErr && pins.current) pin.mutate(); }}>
        <p className="font-semibold">Change my PIN</p>
        <div className="grid grid-cols-3 gap-2">
          {([['current', 'Current'], ['next', 'New'], ['again', 'New again']] as const).map(([k, label]) => (
            <Field key={k} label={label}><input className={pinCls} type="password" inputMode="numeric" maxLength={k === 'current' ? 12 : 6}
              autoComplete={k === 'current' ? 'current-password' : 'new-password'} value={pins[k]} onChange={(e) => { pin.reset(); setPins({ ...pins, [k]: e.target.value.replace(/\D/g, '') }); }} /></Field>
          ))}
        </div>
        {pins.next && pinErr && <p className="text-xs text-critical-600">{pinErr}</p>}
        {pin.isError && <p className="text-sm font-medium text-critical-600">{getApiErrorMessage(pin.error)}</p>}
        {pin.isSuccess && <p className="text-sm font-medium text-success-700">PIN changed. Use the new PIN next time you sign in.</p>}
        <button type="submit" disabled={!!pinErr || !pins.current || pin.isPending} className="rounded-lg border border-brand-green-600 py-2.5 text-sm font-semibold text-brand-green-800 disabled:border-ink-300 disabled:text-ink-400">
          {pin.isPending ? 'Changing…' : 'Change PIN'}
        </button>
      </form>
    </div>
  );
}

function Dashboard() {
  const qc = useQueryClient();
  const welcome = !!(useLocation().state as { welcome?: boolean } | null)?.welcome;
  const [tab, setTab] = useState<'home' | 'milk' | 'payments' | 'me'>('home');
  const [changing, setChanging] = useState(false);
  const [paying, setPaying] = useState(false);
  const me = useQuery({ queryKey: ['cust', 'me'], queryFn: customerApi.me });
  const qr = useQuery({ queryKey: ['cust', 'qr'], queryFn: customerApi.qr });
  const account = useQuery({ queryKey: ['cust', 'account'], queryFn: customerApi.account });
  const plan = useQuery({ queryKey: ['cust', 'changes'], queryFn: customerApi.dayChanges, retry: 0 });
  const milk = useQuery({ queryKey: ['cust', 'milk'], queryFn: customerApi.milk });
  const payments = useQuery({ queryKey: ['cust', 'payments'], queryFn: customerApi.payments });
  const undo = useMutation({ mutationFn: (id: string) => customerApi.undoChange(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['cust', 'changes'] }) });

  // The delivery code changes at 12 noon and at midnight - fetch the new one by itself.
  useEffect(() => {
    if (!qr.data?.valid_until) return;
    const ms = new Date(qr.data.valid_until).getTime() - Date.now() + 2000;
    if (ms <= 0) return;
    const t = setTimeout(() => qr.refetch(), Math.min(ms, 2 ** 31 - 1));
    return () => clearTimeout(t);
  }, [qr.data?.valid_until]); // eslint-disable-line react-hooks/exhaustive-deps

  const a = account.data;
  const prepaid = a?.billing_mode === 'Prepaid';
  const due = Math.max(0, Number(a?.balance_due ?? 0));
  const advance = Math.max(0, -Number(a?.balance_due ?? 0));
  const note = a ? notice(a) : null;
  const err = [me, qr, account, milk, payments].find((q) => q.isError)?.error;

  const milkRow = (m: { id: string; sale_date: string; shift?: string | null; litres: string; amount: string; is_subscription: boolean }) => (
    <li key={m.id} className="flex items-center justify-between rounded-lg bg-white px-3 py-2.5">
      <span className="flex items-center gap-2.5">
        {m.shift === 'Evening' ? <Moon className="h-5 w-5 text-brand-green-800" /> : <Sun className="h-5 w-5 text-brand-gold-600" />}
        <span><span className="block text-sm font-semibold">{day(m.sale_date)}{m.shift ? ` · ${m.shift === 'Evening' ? 'PM' : 'AM'}` : ''}</span>
          <span className="block text-xs text-ink-500">{Number(m.litres).toFixed(2)} L{m.is_subscription ? ' · Subscription' : ''}</span></span>
      </span>
      <span className="font-bold tabular-nums">{prepaid ? '−' : ''}{rupees(m.amount)}</span>
    </li>
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-brand-green-900">{me.data?.name ?? '…'}</h1>
          <p className="text-xs text-ink-500">{me.data?.customer_code}</p>
        </div>
        <button type="button" onClick={() => { customerApi.logout(); qc.removeQueries({ queryKey: ['cust'] }); }} className="inline-flex items-center gap-1 rounded-full border border-ink-300/40 bg-white px-3 py-1.5 text-xs font-semibold text-ink-600">
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>
      {err && <p className="rounded-lg bg-critical-50 p-3 text-sm text-critical-700">{getApiErrorMessage(err, 'Could not load your account.')}</p>}

      {welcome && <p className="rounded-lg bg-success-50 p-3 text-sm font-semibold text-success-700">Welcome, your account is ready! Add this page to your home screen (browser menu → Add to Home screen) to open it like an app.</p>}

      <div className="grid grid-cols-4 gap-1 rounded-xl bg-white p-1">
        {(['home', 'milk', 'payments', 'me'] as const).map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)} className={cn('rounded-lg py-2 text-sm font-semibold', tab === t ? 'bg-brand-green-700 text-white' : 'text-ink-500')}>
            {t === 'home' ? 'Home' : t === 'milk' ? 'My milk' : t === 'payments' ? 'Payments' : 'Profile'}
          </button>
        ))}
      </div>

      {tab === 'home' && me.data && !me.data.subscription_active && <StartMilk />}

      {tab === 'home' && me.data?.subscription_active && (
        <>
          <section className="flex flex-col items-center gap-2 rounded-xl bg-white p-4 text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-ink-600">Today's delivery code</p>
            {qr.data && (
              <span className={cn('inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-bold', qr.data.shift === 'Morning' ? 'bg-warning-50 text-brand-gold-700' : 'bg-brand-green-900 text-white')}>
                {qr.data.shift === 'Morning' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />} {qr.data.shift === 'Morning' ? 'Morning (AM)' : 'Evening (PM)'}
              </span>
            )}
            {qr.data ? <QRCodeSVG value={qr.data.token} size={200} /> : <div className="h-[200px] w-[200px] animate-pulse rounded bg-surface-muted" />}
            <p className="text-xs text-ink-500">Show this to your delivery agent. It changes at 12 noon and every day — always show the one on screen now.</p>
          </section>

          {!!plan.data?.plan.length && (
            <section className="flex flex-col gap-2 rounded-xl bg-white p-4">
              <p className="text-sm font-semibold">My delivery</p>
              <p className="text-sm text-ink-600">{plan.data.plan.map((p) => `${p.shift === 'Morning' ? 'AM' : 'PM'} ${Number(p.litres ?? 0)} L`).join(' + ')} every day</p>
              {plan.data.changes.map((c) => (
                <div key={c.id} className="flex items-center gap-2 rounded-lg bg-warning-50 px-3 py-2 text-sm">
                  <span className="flex-1"><b>{day(c.change_date)}</b> · {c.summary}</span>
                  <button type="button" onClick={() => { if (window.confirm('Undo this change?')) undo.mutate(c.id); }} className="inline-flex items-center gap-1 text-xs font-bold text-critical-600"><Undo2 className="h-3.5 w-3.5" /> Undo</button>
                </div>
              ))}
              {changing ? <ChangeDelivery plan={plan.data.plan} onDone={() => setChanging(false)} />
                : <button type="button" onClick={() => setChanging(true)} className="rounded-lg border border-brand-green-600 py-2 text-sm font-semibold text-brand-green-800">Change delivery (away, AM↔PM, more / less)</button>}
            </section>
          )}

          {note && <p className="rounded-lg bg-warning-50 p-3 text-sm font-semibold text-warning-700">{note}</p>}
          <section className="rounded-xl bg-brand-green-800 p-4 text-white">
            {!a ? <div className="h-16 animate-pulse rounded bg-white/10" /> : prepaid ? (
              <>
                <p className="text-xs font-semibold text-brand-green-100">Wallet balance</p>
                <p className="text-3xl font-bold tabular-nums">{rupees(a.wallet_balance ?? 0)}</p>
                {a.days_remaining != null && <p className="text-xs text-brand-green-100">About {a.days_remaining} day{a.days_remaining === 1 ? '' : 's'} of deliveries left</p>}
              </>
            ) : (
              <>
                <p className="text-xs font-semibold text-brand-green-100">{advance > 0 ? 'Advance paid' : 'Amount due'}</p>
                <p className="text-3xl font-bold tabular-nums">{rupees(advance > 0 ? advance : due)}</p>
                {a.next_due_date && <p className="text-xs text-brand-green-100">Next due date: {day(a.next_due_date)}</p>}
                <p className="text-xs text-brand-green-100">This {a.billing_cycle === 'Weekly' ? 'week' : 'month'} so far: {rupees(a.current_cycle_amount ?? 0)}</p>
              </>
            )}
            {a && !paying && (
              <button type="button" onClick={() => setPaying(true)} className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-brand-gold-400 px-4 py-2 text-sm font-bold text-brand-green-950">
                <Wallet className="h-4 w-4" /> {prepaid ? 'Recharge by UPI' : 'Pay now by UPI'}
              </button>
            )}
          </section>
          {a && paying && <PayNow account={a} />}

          <div className="flex items-center justify-between"><p className="text-sm font-semibold">Recent milk</p>
            <button type="button" className="text-sm font-semibold text-brand-green-700" onClick={() => setTab('milk')}>See all ›</button></div>
          <ul className="flex flex-col gap-1.5">
            {milk.data?.length ? milk.data.slice(0, 3).map(milkRow) : <p className="rounded-lg bg-white p-4 text-center text-sm text-ink-500">No deliveries recorded yet.</p>}
          </ul>
        </>
      )}

      {tab === 'me' && <ProfileTab />}

      {tab === 'milk' && (
        <ul className="flex flex-col gap-1.5">
          {milk.data?.length ? milk.data.map(milkRow) : <p className="rounded-lg bg-white p-6 text-center text-sm text-ink-500">No deliveries recorded yet.</p>}
        </ul>
      )}

      {tab === 'payments' && (
        <>
          <PayNow account={a ?? ({ billing_mode: 'Postpaid', status: 'Ok', balance_due: '0' } as Account)} />
          <ul className="flex flex-col gap-1.5">
            {payments.data?.length ? payments.data.map((p) => (
              <li key={p.id} className="flex items-center justify-between rounded-lg bg-white px-3 py-2.5">
                <span><span className="block text-sm font-semibold">{day(p.paid_at)}</span><span className="block text-xs text-ink-500">{METHOD[p.method] ?? p.method}{p.notes ? ` · ${p.notes}` : ''}</span></span>
                <span className="font-bold tabular-nums text-success-700">+{rupees(p.amount)}</span>
              </li>
            )) : <p className="rounded-lg bg-white p-6 text-center text-sm text-ink-500">No payments recorded yet.</p>}
          </ul>
        </>
      )}
    </div>
  );
}

export default function AccountPage() {
  return useCustomerToken() ? <Dashboard /> : <Login />;
}
